import React, { useState, useEffect } from 'react';
import { AuditData, createAudit, getCreditCheckSummary, getClaim, Claim } from '../api/claims';
import './AuditForm.css';

interface AuditFormProps {
  claimId: string;
  onClose: () => void;
  onSuccess: () => void;
  onOpenPdf?: (url: string, fileName: string) => void;
  isHidden?: boolean;
}

interface CreditCheckSummaryData {
  summary: {
    overall_decision: string;
    identity_verified: boolean;
    credit_accounts_found: number;
    total_balance: string;
    worst_status: string;
    pep_sanctions_clear: boolean;
    electoral_roll_confirmed: boolean;
  };
  applicant: {
    name: string;
    date_of_birth: string;
    age: string;
    address: string;
  };
  creditAccounts: Array<{
    lender: string;
    account_type: string;
    status: string;
    balance: string;
  }>;
  electoralRoll: {
    confirmed: boolean;
    years_at_address: string;
  };
}

export const AuditForm: React.FC<AuditFormProps> = ({
  claimId,
  onClose,
  onSuccess,
  onOpenPdf,
  isHidden = false
}) => {
  const [formData, setFormData] = useState<AuditData>({
    // DBA Section
    dba_signed_and_on_file: undefined,
    dba_printed_name_signature_match_file: undefined,
    dba_address_match_file: undefined,
    dba_terms_of_business_agree_to_dba: undefined,
    
    // FOA Section
    foa_signed_and_on_file: undefined,
    foa_and_dba_signature_match: undefined,
    
    // KYC Section
    kyc_credit_check_on_file: undefined,
    kyc_name_match: undefined,
    kyc_address_match: undefined,
    kyc_pep_sanctions_clear: undefined,
    kyc_electoral_roll_confirmed: undefined,
    kyc_active_credit_accounts_found: undefined,
    
    // Notes
    notes: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState<string>('');
  const [isLoadingData, setIsLoadingData] = useState(true);
  const [creditCheckSummary, setCreditCheckSummary] = useState<CreditCheckSummaryData | null>(null);
  const [claim, setClaim] = useState<Claim | null>(null);
  const [creditCheckError, setCreditCheckError] = useState<string | null>(null);

  // Fetch claim data and credit check summary on mount
  useEffect(() => {
    const fetchData = async () => {
      setIsLoadingData(true);
      
      try {
        // Fetch claim data
        const claimData = await getClaim(claimId);
        setClaim(claimData);
        
        // Fetch credit check summary
        try {
          const creditData = await getCreditCheckSummary(claimId);
          setCreditCheckSummary(creditData as any);
        } catch (error) {
          console.error('Failed to fetch credit check summary:', error);
          setCreditCheckError('Credit check data not available');
        }
      } catch (error) {
        console.error('Failed to fetch data:', error);
      } finally {
        setIsLoadingData(false);
      }
    };

    fetchData();
  }, [claimId]);

  const handleInputChange = (field: keyof AuditData, value: boolean | string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // Open document - use PDF viewer for PDFs, fallback to new tab for other types
  const openDocument = (url: string | undefined, documentType: string) => {
    if (url) {
      // Check if it's a PDF
      const isPdf = url.toLowerCase().includes('.pdf');

      if (isPdf) {
        if (onOpenPdf) {
          onOpenPdf(url, `${documentType} Document`);
        } else {
          window.open(url, '_blank');
        }
      } else {
        // For non-PDF documents, open in new tab
        window.open(url, '_blank');
      }
    } else {
      alert(`${documentType} document not available`);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setMessage('');

    try {
      const response = await createAudit(claimId, formData);
      setMessage(response.message || 'Audit created successfully!');

      // Close the form and call success callback after a short delay
      setTimeout(() => {
        onSuccess();
      }, 1500);
      
    } catch (error: any) {
      setMessage(error.response?.data?.error || 'Failed to create audit');
      setIsSubmitting(false); // Re-enable the form if there's an error
    }
  };

  const renderYesNoField = (
    label: string,
    field: keyof AuditData,
    value: boolean | undefined
  ) => (
    <div className="audit-field">
      <label className="audit-label">{label}</label>
      <div className="radio-group">
        <label className="radio-option">
          <input
            type="radio"
            name={field}
            checked={value === true}
            onChange={() => handleInputChange(field, true)}
          />
          Yes
        </label>
        <label className="radio-option">
          <input
            type="radio"
            name={field}
            checked={value === false}
            onChange={() => handleInputChange(field, false)}
          />
          No
        </label>
      </div>
    </div>
  );

  return (
    <div
      className={`audit-form-modal-overlay${isHidden ? ' audit-form-modal-overlay--hidden' : ''}`}
      onClick={onClose}
    >
      <div className="audit-form-modal audit-form-split-view" onClick={(e) => e.stopPropagation()}>
        <div className="audit-form-header">
          <h2>Claim Audit - {claimId}</h2>
          <button className="close-button" onClick={onClose}>
            &times;
          </button>
        </div>

        <div className="audit-form-content-split">
          {/* Left Panel - Reference Data */}
          <div className="audit-reference-panel">
            <h3 className="reference-panel-title">Reference Information</h3>
            
            {isLoadingData ? (
              <div className="loading-message">Loading reference data...</div>
            ) : (
              <>
                {/* Document Quick Access */}
                <div className="reference-section">
                  <h4>Documents</h4>
                  <div className="document-buttons">
                    <button
                      type="button"
                      className="document-button"
                      onClick={() => openDocument(claim?.formOfAuthorityUrl, 'FOA')}
                      disabled={!claim?.formOfAuthorityUrl}
                    >
                      📄 View FOA
                    </button>
                    <button
                      type="button"
                      className="document-button"
                      onClick={() => openDocument(claim?.damageBasedAgreementUrl, 'DBA')}
                      disabled={!claim?.damageBasedAgreementUrl}
                    >
                      📄 View DBA
                    </button>
                  </div>
                </div>

                {/* Credit Check Summary */}
                {creditCheckError ? (
                  <div className="reference-section">
                    <h4>Credit Check Summary</h4>
                    <p className="error-message">{creditCheckError}</p>
                  </div>
                ) : creditCheckSummary && (
                  <>
                    <div className="reference-section">
                      <h4>Credit Check Summary</h4>
                      <div className="credit-summary-grid">
                        <div className="summary-item">
                          <span className="summary-label">Decision:</span>
                          <span className={`summary-value decision-${creditCheckSummary.summary.overall_decision.toLowerCase()}`}>
                            {creditCheckSummary.summary.overall_decision}
                          </span>
                        </div>
                        <div className="summary-item">
                          <span className="summary-label">Identity Verified:</span>
                          <span className="summary-value">{creditCheckSummary.summary.identity_verified ? '✅ Yes' : '❌ No'}</span>
                        </div>
                        <div className="summary-item">
                          <span className="summary-label">Electoral Roll:</span>
                          <span className="summary-value">{creditCheckSummary.summary.electoral_roll_confirmed ? '✅ Yes' : '❌ No'}</span>
                        </div>
                        <div className="summary-item">
                          <span className="summary-label">PEP/Sanctions:</span>
                          <span className="summary-value">{creditCheckSummary.summary.pep_sanctions_clear ? '✅ Clear' : '❌ Not Clear'}</span>
                        </div>
                        <div className="summary-item">
                          <span className="summary-label">Active Accounts:</span>
                          <span className="summary-value">{creditCheckSummary.summary.credit_accounts_found}</span>
                        </div>
                        <div className="summary-item">
                          <span className="summary-label">Total Balance:</span>
                          <span className="summary-value">{creditCheckSummary.summary.total_balance}</span>
                        </div>
                      </div>
                    </div>

                    <div className="reference-section">
                      <h4>Applicant Information</h4>
                      <div className="applicant-info">
                        <p><strong>Name:</strong> {creditCheckSummary.applicant.name}</p>
                        <p><strong>Date of Birth:</strong> {creditCheckSummary.applicant.date_of_birth} (Age: {creditCheckSummary.applicant.age})</p>
                        <div className="address-comparison">
                          <div>
                            <p><strong>Address on File:</strong></p>
                            <p className="address-text">
                              {claim?.addressLine1 && <>{claim.addressLine1}<br /></>}
                              {claim?.addressLine2 && <>{claim.addressLine2}<br /></>}
                              {claim?.town && <>{claim.town}<br /></>}
                              {claim?.county && <>{claim.county}<br /></>}
                              {claim?.postcode}
                            </p>
                          </div>
                          <div>
                            <p><strong>Credit Check Address:</strong></p>
                            <p className="address-text">{creditCheckSummary.applicant.address}</p>
                          </div>
                        </div>
                      </div>
                    </div>

                    {creditCheckSummary.creditAccounts.length > 0 && (
                      <div className="reference-section">
                        <h4>Credit Accounts ({creditCheckSummary.creditAccounts.length})</h4>
                        <div className="accounts-list">
                          {creditCheckSummary.creditAccounts.slice(0, 5).map((account, index) => (
                            <div key={index} className="account-item">
                              <strong>{account.lender}</strong>
                              <span className="account-details">
                                {account.account_type} - {account.status} - {account.balance}
                              </span>
                            </div>
                          ))}
                          {creditCheckSummary.creditAccounts.length > 5 && (
                            <p className="more-accounts">...and {creditCheckSummary.creditAccounts.length - 5} more accounts</p>
                          )}
                        </div>
                      </div>
                    )}
                  </>
                )}
              </>
            )}
          </div>

          {/* Right Panel - Audit Form */}
          <div className="audit-form-panel">
            <form onSubmit={handleSubmit}>
              {/* DBA Section */}
              <div className="audit-section">
                <h3 className="section-title">DBA</h3>
                {renderYesNoField(
                  "DBA signed and on file?",
                  "dba_signed_and_on_file",
                  formData.dba_signed_and_on_file
                )}
                {renderYesNoField(
                  "Does DBA printed name/signature match file?",
                  "dba_printed_name_signature_match_file",
                  formData.dba_printed_name_signature_match_file
                )}
                {renderYesNoField(
                  "Does DBA address match file?",
                  "dba_address_match_file",
                  formData.dba_address_match_file
                )}
                {renderYesNoField(
                  "Terms of business attached to DBA?",
                  "dba_terms_of_business_agree_to_dba",
                  formData.dba_terms_of_business_agree_to_dba
                )}
              </div>

              {/* FOA Section */}
              <div className="audit-section">
                <h3 className="section-title">FOA</h3>
                {renderYesNoField(
                  "FOA signed and on file?",
                  "foa_signed_and_on_file",
                  formData.foa_signed_and_on_file
                )}
                {renderYesNoField(
                  "FOA and DBA signature match?",
                  "foa_and_dba_signature_match",
                  formData.foa_and_dba_signature_match
                )}
              </div>

              {/* KYC Section */}
              <div className="audit-section">
                <h3 className="section-title">KYC</h3>
                {renderYesNoField(
                  "Credit check on file?",
                  "kyc_credit_check_on_file",
                  formData.kyc_credit_check_on_file
                )}
                {renderYesNoField(
                  "Name match?",
                  "kyc_name_match",
                  formData.kyc_name_match
                )}
                {renderYesNoField(
                  "Address match?",
                  "kyc_address_match",
                  formData.kyc_address_match
                )}
                {renderYesNoField(
                  "Pep/sanctions clear?",
                  "kyc_pep_sanctions_clear",
                  formData.kyc_pep_sanctions_clear
                )}
                {renderYesNoField(
                  "Electoral roll confirmed?",
                  "kyc_electoral_roll_confirmed",
                  formData.kyc_electoral_roll_confirmed
                )}
                {renderYesNoField(
                  "Active credit accounts found?",
                  "kyc_active_credit_accounts_found",
                  formData.kyc_active_credit_accounts_found
                )}
              </div>

              {/* Notes Section */}
              <div className="audit-section">
                <h3 className="section-title">Additional Notes</h3>
                <div className="audit-field">
                  <textarea
                    className="notes-textarea"
                    placeholder="Enter any additional notes or comments..."
                    value={formData.notes || ''}
                    onChange={(e) => handleInputChange('notes', e.target.value)}
                    rows={4}
                  />
                </div>
              </div>

              {/* Message Display */}
              {message && (
                <div className={`message ${message.includes('success') ? 'success' : 'error'}`}>
                  {message}
                </div>
              )}

              {/* Action Buttons */}
              <div className="audit-form-actions">
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="submit-button"
                >
                  {isSubmitting ? 'Creating Audit...' : 'Create Audit'}
                </button>
                <button
                  type="button"
                  onClick={onClose}
                  disabled={isSubmitting}
                  className="cancel-button"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};
